import React, { useCallback, useState } from 'react';
import api from '../../services/api';
import type {
  ManualCheck,
  ManualCheckListFilters,
} from '../../types/manualCheck';
import { ManualChecksTable } from './ManualChecksTable';

interface Salesman {
  id: number;
  name: string;
}

interface ManualCheckSearchTabProps {
  salesmen: Salesman[];
  firms: string[];
  /**
   * When the user clicks "edit" on a result row, the parent takes over —
   * it flips back to the Recent Entries tab and populates the edit form.
   * Keeps a single source of truth for the create/edit form.
   */
  onEditCheck: (check: ManualCheck) => void;
  /**
   * Fires after a successful delete so the parent can refresh its own
   * Recent Entries list too (they share the underlying data set).
   */
  onDataChanged?: () => void;
}

/**
 * R17 Task G — Check-history search tab.
 *
 * Peter's ask: "search by checks ... sometimes I need to see if someone
 * had sent the check this year etc etc". This tab hosts a dedicated
 * filter form (date range, firm, salesman, amount range) that hits the
 * same `/manual-checks` index endpoint. Results render through the
 * shared `ManualChecksTable` so the row shape stays consistent with
 * Recent Entries.
 *
 * Deliberately owns its own filter + results state (separate from the
 * Recent Entries filters on the parent) so navigating between tabs
 * doesn't wipe either side's context.
 */
export const ManualCheckSearchTab: React.FC<ManualCheckSearchTabProps> = ({
  salesmen,
  firms,
  onEditCheck,
  onDataChanged,
}) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [salesmanId, setSalesmanId] = useState<number | ''>('');
  const [firm, setFirm] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');

  const [results, setResults] = useState<ManualCheck[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = useCallback(async () => {
    // Validate amount range before hitting the wire — the API will 422,
    // but a client-side hint is friendlier.
    const minNum = minAmount === '' ? undefined : Number(minAmount);
    const maxNum = maxAmount === '' ? undefined : Number(maxAmount);
    if (minNum !== undefined && (Number.isNaN(minNum) || minNum < 0)) {
      setError('Min amount must be a non-negative number.');
      return;
    }
    if (maxNum !== undefined && (Number.isNaN(maxNum) || maxNum < 0)) {
      setError('Max amount must be a non-negative number.');
      return;
    }
    if (minNum !== undefined && maxNum !== undefined && minNum > maxNum) {
      setError('Min amount cannot exceed max amount.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters: ManualCheckListFilters = {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        salesman_user_id: salesmanId === '' ? undefined : Number(salesmanId),
        firm_name: firm || undefined,
        min_amount: minNum,
        max_amount: maxNum,
        per_page: 100,
      };
      const resp = await api.getManualChecks(filters);
      setResults(resp.data ?? []);
      setHasSearched(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Search failed. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, salesmanId, firm, minAmount, maxAmount]);

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setSalesmanId('');
    setFirm('');
    setMinAmount('');
    setMaxAmount('');
    setResults([]);
    setHasSearched(false);
    setError(null);
  };

  const handleDelete = async (check: ManualCheck) => {
    if (
      !window.confirm(
        `Delete manual check for ${check.firm_name} on ${check.check_date}?`,
      )
    ) {
      return;
    }
    try {
      await api.deleteManualCheck(check.id);
      // Refresh both the search results and any parent view that mirrors
      // the underlying dataset.
      await runSearch();
      onDataChanged?.();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Delete failed',
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch();
  };

  const totalAmount = results.reduce(
    (acc, c) => acc + Number(c.amount || 0),
    0,
  );
  const totalSalesmanCommission = results.reduce(
    (acc, c) => acc + Number(c.salesman_commission || 0),
    0,
  );

  return (
    <div className="space-y-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow rounded-lg p-4 space-y-4"
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <label className="text-sm">
            <span className="text-gray-700">Start date</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">End date</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Salesman</span>
            <select
              value={salesmanId}
              onChange={(e) =>
                setSalesmanId(e.target.value ? Number(e.target.value) : '')
              }
              className="mt-1 w-full border rounded px-2 py-1"
            >
              <option value="">All salesmen</option>
              {salesmen.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Firm</span>
            <input
              type="text"
              list="manual-check-firms-search"
              value={firm}
              onChange={(e) => setFirm(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1"
              placeholder="e.g. Acme"
            />
            <datalist id="manual-check-firms-search">
              {firms.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Min amount</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1"
              placeholder="e.g. 500"
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Max amount</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="mt-1 w-full border rounded px-2 py-1"
              placeholder="e.g. 5000"
            />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-60"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm"
          >
            Reset
          </button>
          <p className="text-xs text-gray-500 ml-2">
            All filters optional. Leave blank to broaden the search.
          </p>
        </div>
      </form>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      <section className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900">
            Search Results
            {hasSearched && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({results.length} check{results.length === 1 ? '' : 's'})
              </span>
            )}
          </h2>
          {hasSearched && results.length > 0 && (
            <div className="text-xs text-gray-600 flex gap-4">
              <span>
                Total: <strong>${totalAmount.toFixed(2)}</strong>
              </span>
              <span>
                Salesman commission:{' '}
                <strong>${totalSalesmanCommission.toFixed(2)}</strong>
              </span>
            </div>
          )}
        </div>
        {!hasSearched ? (
          <div className="text-sm text-gray-500 italic p-4">
            Set your filters and click Search to look up check history.
          </div>
        ) : (
          <ManualChecksTable
            checks={results}
            onEdit={onEditCheck}
            onDelete={handleDelete}
          />
        )}
      </section>
    </div>
  );
};

export default ManualCheckSearchTab;
